import { useEffect, useState } from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import { Card, Drawer, Empty, Flex, Spin, Tag, Typography } from "antd";
import { ContactData } from "@/pages/contacts/ContactData";
import { AgendaPanel } from "./AgendaPanel";
import {
  EditOutlined,
  FlagOutlined,
  MailOutlined,
  ProfileOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ContactDetail,
  ContactMatch,
  contactsApi,
  phoneFromThread,
} from "@/services/contacts.api";
import { ConversationListItem } from "@/services/conversations.api";
import { SellerIcon } from "@/components";
import { axiosInstance, displayEmail } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { CustomerIcon } from "@/components/icon/CustomerIcon";
import { HandshakeIcon } from "@/components/icon/HandshakeIcon";

const { Text } = Typography;
const GREEN = "#00733B";

/** Side panel "Agenda": Contact data + Additional data + Agenda + Groups + Relations. */
export const ContactPanel = ({
  open,
  onClose,
  conversation,
  storeCode,
}: {
  open: boolean;
  onClose: () => void;
  conversation?: ConversationListItem;
  storeCode: string;
}) => {
  const { push } = useNavigation();
  const t = useTranslate();
  const [contact, setContact] = useState<ContactMatch | null>(null);
  const [detail, setDetail] = useState<ContactDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [addressCount, setAddressCount] = useState(0);

  useEffect(() => {
    const userId = contact?.userId ?? detail?.userId;
    if (!userId || !open) { setAddressCount(0); return; }
    let active = true;
    axiosInstance
      .get(`${API_BASE_URL}/addresses`, { params: { user_id: userId, store: storeCode } })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        setAddressCount(rows.length);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [contact, detail, open, storeCode]);

  useEffect(() => {
    if (!open || !conversation) return;
    let active = true;
    setContact(null);
    setDetail(null);
    setLoading(true);
    contactsApi
      .findByPhone(conversation.external_thread_id, storeCode)
      .then(async (c) => {
        if (!active) return;
        setContact(c);
        if (c?.contactId) {
          const d = await contactsApi.getContactDetail(c.contactId).catch(() => null);
          if (active) setDetail(d);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, conversation, storeCode]);

  const phone = conversation ? phoneFromThread(conversation.external_thread_id) : "";
  const title = contact?.name ?? conversation?.contact_name ?? phone;
  const additional = Object.entries(detail?.additionalData ?? {});
  const storeRels = (detail?.participations ?? []).filter((p) => p.type === "store");
  const sellerRels = (detail?.participations ?? []).filter((p) => p.type === "seller");
  const customerRels = (detail?.participations ?? []).filter((p) => p.type === "customer");
  const sellerGroups = [...new Set(sellerRels.map((p) => p.identifier))];
  const relCount = storeRels.length + sellerRels.length + customerRels.length;

  const displayStore = storeCode
    ? storeCode.charAt(0).toUpperCase() + storeCode.slice(1)
    : storeCode;

  const cardHeaderStyle = { background: "#f5f5f5", borderBottom: "1px solid #f0f0f0" };
  const cardStyle = { border: "1px solid #f0f0f0", borderRadius: 8 };

  const row = (icon: React.ReactNode, value?: string, bold?: boolean) => (
    <Flex align="center" gap={8} style={{ padding: "3px 0" }}>
      <span style={{ color: "#8c8c8c", fontSize: 14 }}>{icon}</span>
      <Text strong={bold}>{value || "—"}</Text>
    </Flex>
  );

  const rolePill = (role?: string) => (
    <span
      style={{
        fontSize: 13,
        color: "#8c8c8c",
        whiteSpace: "nowrap",
      }}
    >
      {role || "—"}
    </span>
  );

  const relRow = (key: React.Key, name: string, role?: string) => (
    <Flex key={key} align="center" justify="space-between" style={{ padding: "6px 0" }}>
      <Text>{name}</Text>
      {rolePill(role)}
    </Flex>
  );

  const subSectionHeader = (icon: React.ReactNode, label: string) => (
    <Flex
      align="center"
      gap={6}
      style={{
        background: "#f5f5f5",
        padding: "0 8px",
        height: 36,
        borderRadius: "8px 8px 0 0",
        border: "1px solid #f0f0f0",
        borderBottom: "none",
      }}
    >
      <span style={{ fontSize: 14, color: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center" }}>{icon}</span>
      <Text strong style={{ fontSize: 13 }}>
        {label}
      </Text>
    </Flex>
  );

  const subSectionBody = (children: React.ReactNode, isEmpty?: boolean) => (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderTop: "none",
        borderRadius: "0 0 8px 8px",
        padding: "0 8px",
      }}
    >
      {isEmpty ? (
        <Text
          type="secondary"
          italic
          style={{ display: "block", padding: "8px 0", fontSize: 13 }}
        >
          El contacto no tiene relaciones.
        </Text>
      ) : (
        children
      )}
    </div>
  );

  const sectionTitle = (
    icon: React.ReactNode,
    label: string,
    count?: number,
    green = true,
    extra?: React.ReactNode,
  ) => (
    <Flex align="center" justify="space-between">
      <Flex align="center" gap={6}>
        {icon}
        <Text strong style={green ? { color: GREEN } : {}}>
          {label}
        </Text>
        {extra}
      </Flex>
      {count !== undefined && <Text type="secondary">{count}</Text>}
    </Flex>
  );

  return (
    <>
    <Drawer
      title={t("contacts.data")}
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
      styles={{ body: { background: "transparent" } }}
    >
      {loading ? (
        <div style={{ textAlign: "center", paddingTop: 40 }}>
          <Spin />
        </div>
      ) : (
        <Flex vertical gap={12}>
          {/* Datos de contacto + Agenda */}
          <Card
            size="small"
            style={cardStyle}
            title={
              <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                <Flex align="center" gap={6}>
                  <UserOutlined style={{ color: GREEN }} />
                  <Text
                    strong
                    style={{ color: GREEN, cursor: contact?.contactId ? "pointer" : "default" }}
                    onClick={() => {
                      if (!contact?.contactId) return;
                      if (conversation?.conversation_id) {
                        const url = new URL(window.location.href);
                        url.searchParams.set("open", conversation.conversation_id);
                        window.history.replaceState(null, "", url.toString());
                      }
                      push(`/contacts/${contact.contactId}`);
                    }}
                  >
                    {title}
                  </Text>
                  {contact?.contactId && (
                    <EditOutlined
                      style={{ color: "#8c8c8c", fontSize: 13, cursor: "pointer" }}
                      onClick={() => setEditOpen(true)}
                    />
                  )}
                </Flex>
                {detail?.createdAt && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, fontWeight: 400, textAlign: "right", lineHeight: 1.3 }}
                  >
                    Cuenta creada<br />
                    {dayjs(detail.createdAt).format("DD/MM/YY, h:mm a")}
                  </Text>
                )}
              </Flex>
            }
            styles={{ header: cardHeaderStyle }}
          >
            {row(<ShopOutlined />, displayStore + ":", true)}
            {row(<MailOutlined />, displayEmail(contact?.email))}
            {row(<WhatsAppOutlined />, contact?.whatsapp ?? phone)}
            {row(<FlagOutlined />, contact?.company ?? detail?.company ?? conversation?.contact_company ?? undefined)}

            {!contact?.contactId && (
              <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                Sin contacto registrado para este número.
              </Text>
            )}

            {/* Additional data sub-section */}
            {contact?.contactId && (
              <>
                <div style={{ borderTop: "1px solid #f0f0f0", margin: "8px 0" }} />
                <Text strong style={{ display: "block", marginBottom: 6 }}>
                  Datos adicionales
                </Text>
                {additional.length > 0 ? (
                  additional.map(([k, v]) => (
                    <Flex key={k} align="center" gap={8} style={{ padding: "3px 0" }}>
                      <Text style={{ fontSize: 13 }}>• {k}: &nbsp;{String(v)}</Text>
                    </Flex>
                  ))
                ) : (
                  <Text type="secondary" style={{ fontSize: 13 }}>Sin datos adicionales</Text>
                )}
              </>
            )}

            {/* Agenda sub-row */}
            {contact?.contactId && (
              <Flex
                align="center"
                justify="space-between"
                onClick={() => setAgendaOpen(true)}
                style={{
                  background: "#f5f5f5",
                  borderTop: "1px solid #f0f0f0",
                  margin: "8px -12px -12px",
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                <Flex align="center" gap={6}>
                  <ProfileOutlined style={{ color: GREEN }} />
                  <Text strong style={{ color: GREEN }}>
                    Agenda
                  </Text>
                </Flex>
                <Text type="secondary">{addressCount}</Text>
              </Flex>
            )}
          </Card>

          {/* Groups */}
          {detail && detail.groups.length > 0 && (
            <Card
              size="small"
              style={cardStyle}
              title={sectionTitle(
                <TeamOutlined />,
                "Grupos",
                detail.groups.length,
                false,
              )}
              styles={{ header: cardHeaderStyle }}
            >
              <Flex gap={6} wrap="wrap">
                {detail.groups.map((g, i) => (
                  <Tag
                    key={i}
                    icon={<TeamOutlined />}
                    style={{
                      background: "rgba(0,0,0,0.06)",
                      border: "none",
                      borderRadius: 4,
                      color: "rgba(0,0,0,0.65)",
                    }}
                  >
                    {g.name}
                  </Tag>
                ))}
              </Flex>
            </Card>
          )}

          {/* Relations */}
          {detail && contact?.contactId && (
            <Card
              size="small"
              style={cardStyle}
              title={sectionTitle(
                <HandshakeIcon style={{ fontSize: 16 }} />,
                "Relaciones",
                relCount,
                false,
              )}
              styles={{ header: cardHeaderStyle }}
            >
              {/* Customer */}
              <div style={{ marginBottom: 10 }}>
                {subSectionHeader(
                  <CustomerIcon style={{ width: 14, height: 11 }} />,
                  "Cliente",
                )}
                {subSectionBody(
                  customerRels.map((p, i) =>
                    relRow(`customer-${p.identifier}-${i}`, p.name, p.role),
                  ),
                  customerRels.length === 0,
                )}
              </div>

              {/* Store */}
              {storeRels.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {subSectionHeader(<ShopOutlined style={{ fontSize: 14 }} />, "Store")}
                  {subSectionBody(
                    storeRels.map((p, i) =>
                      relRow(`store-${p.identifier}-${i}`, p.name, p.role),
                    ),
                  )}
                </div>
              )}

              {/* Sellers */}
              {sellerGroups.map((code) => (
                <div key={code} style={{ marginBottom: 10 }}>
                  {subSectionHeader(
                    <SellerIcon style={{ fontSize: 14 }} />,
                    `Seller: ${code}`,
                  )}
                  {subSectionBody(
                    sellerRels
                      .filter((p) => p.identifier === code)
                      .map((p, i) => relRow(`seller-${code}-${i}`, p.name, p.role)),
                  )}
                </div>
              ))}
            </Card>
          )}

          {contact === null && !loading && <Empty description="Sin datos de contacto" />}
        </Flex>
      )}
    </Drawer>

    <AgendaPanel
      open={agendaOpen}
      onClose={() => setAgendaOpen(false)}
      userId={contact?.userId ?? detail?.userId}
      storeCode={storeCode}
    />

    {/* Edit contact drawer */}
    <Drawer
      title="Editar datos de contacto"
      placement="right"
      width={400}
      open={editOpen}
      onClose={() => setEditOpen(false)}
    >
      <ContactData
        isEditing={true}
        onBeginEdit={() => {}}
        onEndEdit={() => setEditOpen(false)}
        closeModal={() => setEditOpen(false)}
        contactId={contact?.contactId ?? undefined}
        userData={{
          userId: contact?.userId ?? detail?.userId,
          firstname: detail?.firstname,
          lastname: detail?.lastname,
          email: contact?.email,
          whatsapp: contact?.whatsapp,
          company: contact?.company ?? detail?.company,
          createdAt: detail?.createdAt,
        }}
        refresh={() => {
          if (!conversation) return;
          contactsApi.findByPhone(conversation.external_thread_id, storeCode).then(async (c) => {
            setContact(c);
            if (c?.contactId) {
              const d = await contactsApi.getContactDetail(c.contactId).catch(() => null);
              setDetail(d);
            }
          });
        }}
      />
    </Drawer>
    </>
  );
};
